<template>
    <Modal @shown="shown" @hidden="hidden" ref="modal" modalClass="modal-md" @submit="submit">
        <template #title>
            <template v-if="company && !readonly">
                {{ i18n('Edit {name}').replace('{name}', i18n('Company')) }}
            </template>
            <template v-else-if="company">
                {{ i18n('{name} Detail').replace('{name}', i18n('Company')) }}
            </template>
            <template v-else>
                {{ i18n('Add {name}').replace('{name}', i18n('Company')) }}
            </template>
        </template>

        <div class="alert alert-danger mb-2" v-if="errorMessage">{{ errorMessage }}</div>

        <div class="row g-2">
            <div class="col-12 col-sm-6">
                <Field v-model="formData.code" :errorMessage="errorMessages.code" :label="i18n('Code')"
                    :disabled="submitted || !!company" :required="true" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.alias" :errorMessage="errorMessages.alias" :label="i18n('Alias')"
                    :disabled="submitted" :required="true" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12">
                <Field v-model="formData.name" :errorMessage="errorMessages.name" :label="i18n('Name')"
                    :disabled="submitted" :required="true" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.is_from_group" :errorMessage="errorMessages.is_from_group"
                    :label="i18n('Of Group')" :disabled="submitted" :type="readonly ? 'readonly' : 'checkbox'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.is_client" :errorMessage="errorMessages.is_client" :label="i18n('Client')"
                    :disabled="submitted" :type="readonly ? 'readonly' : 'checkbox'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.logo.png_url" :errorMessage="errorMessages.logo_png_url"
                    :label="i18n('Logo Png Url')" :disabled="submitted" format="url"
                    :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.logo.light_png_url" :errorMessage="errorMessages.logo_light_png_url"
                    :label="i18n('Logo Light Png Url')" :disabled="submitted" format="url"
                    :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.logo.svg_url" :errorMessage="errorMessages.logo_svg_url"
                    :label="i18n('Logo Svg Url')" :disabled="submitted" format="url"
                    :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.logo.icon_url" :errorMessage="errorMessages.logo_icon_url"
                    :label="i18n('Logo Icon Url')" :disabled="submitted" format="url"
                    :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6">
                <Field v-model="formData.document" :errorMessage="errorMessages.document" :label="i18n('NIF')"
                    :disabled="submitted" :type="readonly ? 'readonly' : 'text'" />
            </div>
        </div>

        <template #footer>
            <div class="row">
                <template v-if="!readonly">
                    <div class="col text-start">
                        <button type="button" class="btn btn-danger" @click="hide">
                            {{ i18n('Cancel') }}
                        </button>
                    </div>
                    <div class="col text-end">
                        <button type="submit" class="btn btn-primary text-white" :disabled="submitted">
                            {{ i18n('Confirm') }}
                        </button>
                    </div>
                </template>
                <div class="col text-end" v-else>
                    <button type="button" @click="hide" class="btn btn-outline-dark btn-sm me-2">
                        {{ i18n('Close') }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import api from "@/api.js";
import { duplicate, } from "@/tools/object.js";
import { validateCif, } from "@/tools/documentType.js";
import Modal from "@/components/common/layout/Modal.vue";
import Field from "@/components/common/form/Field.vue";
import { translatedErrorMessage, } from "@/tools/errorHandling.js";

export default {
    components: {
        Modal,
        Field,
    },
    data() {
        return {
            companyId: null,
            company: null,
            submitted: false,
            formData: {},
            errorMessages: {},
            errorMessage: '',
        };
    },
    emits: [
        'shown',
        'hidden',
        'created',
        'updated',
    ],
    computed: {
        readonly() {
            return this.company
                && this.company.acl?.update !== true;
        },
    },
    methods: {
        show(companyId) {
            this.companyId = companyId;
            this.company = null;
            this.load().then(() => {
                this.submitted = false;
                this.$refs['modal'].show();
                this.errorMessages = {};
                this.errorMessage = '';

                if (this.company) {
                    this.formData = duplicate(this.company);
                } else {
                    this.formData = { logo: {} };
                }
            });
        },
        load() {
            if (!this.companyId) return Promise.resolve();
            let promise = api.get(`companies/${this.companyId}`, { acl: 'company' });

            promise.then(company => {
                this.company = company;
            });

            return promise;
        },
        hide() {
            this.$refs['modal'].hide();
        },
        shown() {
            this.$emit('shown');
        },
        hidden() {
            this.$emit('hidden');
        },
        submit() {
            this.submitted = true;
            this.errorMessages = {};
            this.errorMessage = '';

            const postFormData = duplicate(this.formData);
            let savePromise;

            if (postFormData.document &&
                !validateCif(postFormData.document)) {
                this.errorMessage = this.i18n('Invalid Nif.');
                this.submitted = false;
                return;
            }

            if (!this.company) {
                savePromise = api.post(`companies`, postFormData);
            } else {
                savePromise = api.put(`companies/${this.company.id}`, postFormData);
            }

            savePromise.then(company => {
                if (!this.company) {
                    this.$emit('created', company);
                } else {
                    this.$emit('updated', company);
                }

                this.hide();
                api.clearCache();
            }).catch((errorMessage) => {
                this.errorMessage = translatedErrorMessage(errorMessage);
                this.submitted = false;
            });
        }
    }
}
</script>